'use client'

import { usePathname } from 'next/navigation'
import { useMemo } from 'react'
import { Suspense, type ReactNode } from 'react'

interface ConditionalWrapperProps {
  children: ReactNode
}

/**
 * Client component wrapper that conditionally renders children based on route.
 */
function ConditionalWrapper({ children }: ConditionalWrapperProps) {
  const pathname = usePathname()
  const isAdminRoute = useMemo(
    () => pathname?.startsWith('/admin'),
    [pathname]
  )
  const isLoginPage = useMemo(
    () => pathname === '/admin/login',
    [pathname]
  )

  // Admin routes have their own header/footer in AdminShell, except login page
  if (isAdminRoute && !isLoginPage) {
    return null
  }

  return <>{children}</>
}

/**
 * Conditionally renders site header and footer based on route.
 * Excludes header/footer for admin routes (which have their own in AdminShell),
 * except for the login page which uses the main site design.
 */
export function ConditionalHeaderFooter({ header, footer }: { header: ReactNode; footer: ReactNode }) {
  return (
    <ConditionalWrapper>
      <Suspense fallback={null}>{header}</Suspense>
      {footer}
    </ConditionalWrapper>
  )
}

/**
 * Conditionally renders site header based on route.
 * Excludes header for admin routes (which have their own in AdminShell),
 * except for the login page which uses the main site design.
 */
export function ConditionalHeader({ header }: { header: ReactNode }) {
  return (
    <ConditionalWrapper>
      <Suspense fallback={null}>{header}</Suspense>
    </ConditionalWrapper>
  )
}

/**
 * Conditionally renders site footer based on route.
 * Excludes footer for admin routes (which have their own in AdminShell),
 * except for the login page which uses the main site design.
 */
export function ConditionalFooter({ footer }: { footer: ReactNode }) {
  const pathname = usePathname()
  const isExplanationRoute = useMemo(
    () => pathname?.startsWith('/explanation'),
    [pathname]
  )

  if (isExplanationRoute) {
    return null
  }

  return <ConditionalWrapper>{footer}</ConditionalWrapper>
}

